import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NAME_MAX_LENGTH } from '../../constants/ValidationConstants';
import { Profile } from '../../models/profile';
import { Persona } from '../../models/persona';
import { Country } from '../../models/country';
import { PhoneCode } from '../../models/phone-code';
import { combineLatest, debounceTime, map, Subject, takeUntil } from 'rxjs';
import { ProfileService } from '../../services/profile-service/profile.service';
import { TranslateService } from '@ngx-translate/core';
import { PersonaService } from '../../services/persona-service/persona.service';
import { PhoneCodeService } from '../../services/phone-code-service/phone-code.service';
import { CountryService } from '../../services/country-service/country.service';
import { AuthService } from '../../services/auth.service';
import { ActivatedRoute } from '@angular/router';
import { AddressService } from '../../services/address-service/address.service';
import { config } from '../../../assets/config-constants';
import { MatDialog } from '@angular/material/dialog';
import { DeleteConfimationDialogComponent } from '../../dialogs/delete-confimation-dialog/delete-confimation-dialog.component';

@Component({
  selector: 'prof-progressive-profile-page',
  templateUrl: './progressive-profile-page.component.html',
  styleUrls: ['./progressive-profile-page.component.scss'],
  standalone: false,
})
export class ProgressiveProfilePageComponent implements OnInit, OnDestroy {
  public readonly NAME_MAX_LENGTH = NAME_MAX_LENGTH;

  public profileForm: FormGroup;

  public profile: Profile;
  public personas: Persona[];
  public countries: Country[];
  public phoneCodes: PhoneCode[];
  public saving = false;

  public phoneMask = '';
  public postMask = '';
  public postalCodeURL = '';

  public formDirty = false;
  private _originalProfile: Profile;

  public timeForDeletion: Date;
  private readonly onDestroy$ = new Subject<void>();
  public isFederated: boolean;
  public isInternal: boolean;
  public isChangePasswordDisabled: boolean = false;
  public isDeleteUserDisabled: boolean = false;


  constructor(
    private readonly translate: TranslateService,
    public profSvc: ProfileService,
    private readonly _personaSvc: PersonaService,
    private readonly _countrySvc: CountryService,
    private readonly _countryPhoneCodeSvc: PhoneCodeService,
    private readonly _auth: AuthService,
    private readonly _route: ActivatedRoute,
    private readonly _addressSvc: AddressService,
    private readonly fb: FormBuilder,
    private readonly _dialog :MatDialog
  ) {}

  ngOnInit() {
    this._auth.setOnboarding(false);

    this._route.queryParamMap
      .pipe(map((params) => params.get('route_from')))
      .subscribe((routefrom) => {
        this._auth.routeFrom = routefrom;
      });

    this._auth.isAuthenticated$.subscribe((res) => {
      if (!res) {
        this._auth.login();
      }
    });

    this._auth
      .getUser$()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((prof) => {
        this.isFederated = prof[config.isFederatedUser];
        this.isInternal = prof[config.claimUserType]?.toLowerCase() == 'internal';
      });

    this.profileForm = this.fb.group({
      firstName: [{ value: '', disabled: this.isFederated }, [Validators.required, this.maxLengthCheck(NAME_MAX_LENGTH), this.minLengthCheck(1), Validators.pattern(/^[^!"#$%*+/:;<=>?@[\\\]^_`{|}~]+$/)]],
      lastName: [{ value: '', disabled: this.isFederated }, [Validators.required, this.maxLengthCheck(NAME_MAX_LENGTH), this.minLengthCheck(1), Validators.pattern(/^[^!"#$%*+/:;<=>?@[\\\]^_`{|}~]+$/)]],
      email: [{ value: '', disabled: true }],
      postalCode: ['', Validators.required],
      country: ['' , Validators.required],
      phoneCode: ['', Validators.required],
      phone: [
        '',
        [
          Validators.pattern(/^\d*$/),
          Validators.minLength(7),
          Validators.maxLength(15),
        ],
      ],
      personaID: [null, Validators.required],
      canWeShareInfo: [false],
    });

    this.profSvc.profile$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((data) => {
        this._originalProfile = { ...data };
        this.profile = data;

        if (this.profSvc.internal) {
          this.phoneMask = '(000) 000-0000';
        }

        this.profileForm.patchValue({
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          postalCode: data.postalCode,
          country: data.country,
          phoneCode: data.phoneCode,
          phone: data.phone,
          personaID: data.personaID,
          canWeShareInfo: data.canWeShareInfo,
        });

        const firstNameControl = this.profileForm.get('firstName');
        const lastNameControl = this.profileForm.get('lastName');

        // Show over-length name errors immediately for prefilled profile data.
        if (firstNameControl?.hasError('maxLengthTrimmed')) {
          firstNameControl.markAsTouched();
        }
        if (lastNameControl?.hasError('maxLengthTrimmed')) {
          lastNameControl.markAsTouched();
        }

        if (this.isFederated) {
          this.profileForm.get('firstName').disable();
          this.profileForm.get('lastName').disable();
        }
      });

    this._personaSvc.personas$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((data) => (this.personas = data));

    this._countrySvc.countries$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((data) => (this.countries = data));

    this._countryPhoneCodeSvc.codes$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((data: PhoneCode[]) => (this.phoneCodes = data));
    this.determineStateProvince();
    this.watchPhoneInput();
  }

  ngOnDestroy() {
    this.onDestroy$.next();
  }

  save() {
    if (this.profileForm.invalid) {
      return;
    }
    this.saving = true;

    const formValues = this.profileForm.getRawValue();
    formValues.firstName = formValues.firstName.trim();
    formValues.lastName = formValues.lastName.trim();

    this.profileForm.patchValue({
      firstName: formValues.firstName,
      lastName: formValues.lastName
    }, { emitEvent: false });

    Object.assign(this.profile, formValues);
    this.profSvc.saveProfile().subscribe({
      next: () => {
        this.saving = false;
        this._originalProfile = { ...this.profile };
        this.profileForm.markAsPristine();
        this.formDirty = false;
      },
      error: () => {
        this.saving = false;
      }
    });
  }

  convertLanguage(id): string {
    const value = 'PERSONAS.' + id;
    return this.translate.instant(value);
  }

  resetProfile() {
    if (!this._originalProfile) return;

    this.profileForm.reset({
      firstName: this._originalProfile.firstName,
      lastName: this._originalProfile.lastName,
      email: this._originalProfile.email,
      postalCode: this._originalProfile.postalCode,
      country: this._originalProfile.country,
      phoneCode: this._originalProfile.phoneCode,
      phone: this._originalProfile.phone,
      personaID: this._originalProfile.personaID,
      canWeShareInfo: this._originalProfile.canWeShareInfo,
    });

    this.formDirty = false;
  }

  /*
   * Watches postal code and country inputs and then calls a service for fill in state/province automatically
   */
  private determineStateProvince() {
    const postalInput$ = this.profileForm.get('postalCode').valueChanges.pipe(debounceTime(200));
    const countryInput$ = this.profileForm.get('country').valueChanges;

    combineLatest([postalInput$, countryInput$, this._countrySvc.countries$])
      .pipe(
        map(([postalCodeSelection, countrySelection, countryList]) => ({
          postalCodeSelection,
          countrySelection,
          countryList,
        })),
        takeUntil(this.onDestroy$)
      )
      .subscribe((data) => {
        this.profile.stateProvince = '';

        const originalCountry = (this._originalProfile?.country || '').trim().toLowerCase();
        const newCountry = (data.countrySelection || '').trim().toLowerCase();

        const originalPostal = (this._originalProfile?.postalCode || '').trim().toLowerCase();
        const newPostal = (data.postalCodeSelection || '').trim().toLowerCase();

        const dataDidChange = [
          originalCountry !== newCountry,
          originalPostal !== newPostal,
        ];

        if (dataDidChange.includes(true)) {
          this.formDirty = true;
        }

        const ctry = data.countryList.find((c) => c.value === data.countrySelection);
        this.postalCodeURL = ctry?.postalCodeURL;
        this._addressSvc.getCityState(ctry?.value, data.postalCodeSelection).subscribe((stdata) => {
          if (stdata?.state) {
            this.profile.stateProvince = stdata.state;
            this.profileForm.get('postalCode').setErrors(null);
          }
          else if (data.postalCodeSelection && ctry?.postalCodeURL) {
          this.profile.stateProvince = '';
          this.profileForm.get('postalCode').setErrors({ 'invalidCombination': true });
          this.profileForm.get('postalCode').markAsTouched(); 
        }
  });

        this.setDefaultCountryCode(data.countrySelection);

        if (ctry && this.profileForm.get('postalCode').enabled) {
          this.profileForm.get('postalCode').setValidators(Validators.required);        

          this.phoneMask = ctry.phoneMask;
          this.postMask = ctry.postMask;
          if(this.phoneMask){
            this.profileForm.get('phone').clearValidators(); 
          }
          else{
            this.profileForm.get('phone').setValidators([
              Validators.pattern(/^\d*$/),
              Validators.minLength(7),
              Validators.maxLength(15),
            ]);
          }

          if (!ctry.postalCodeURL) {
            this.postalCodeURL = '';
            this.profileForm.get('postalCode').setValue('');
            this.profileForm.get('postalCode').disable();
            this.profileForm.get('postalCode').clearValidators();
            this.profile.postalCode = '';
          }
        } 
        else if (ctry?.postalCodeURL) {             
          this.profileForm.get('postalCode').setValidators(Validators.required);
          this.profileForm.get('postalCode').enable();
        }
      });
  }

  /*
   * Watches phone input and tag form as dirty if it changes - needed for mask to work properly
   */
  watchPhoneInput(): void {
    const phoneInput$ = this.profileForm.get('phone').valueChanges;

    phoneInput$.pipe(takeUntil(this.onDestroy$)).subscribe((phoneSelection) => {
      if (this._originalProfile?.phone !== phoneSelection && this.profileForm.get('phone').valid) {
        this.formDirty = true;
      } else {
        this.formDirty = false;
      }
    });
  }

  disableForm() {
    if (this.profileForm) {
      if (this.postalCodeURL) {
        return (
          !this.profileForm.valid ||
          (!this.profileForm.dirty && !this.formDirty) ||
          (!this.profSvc.internal && !this.profile.stateProvince)
        );
      } else {
        return !this.profileForm.valid || (!this.profileForm.dirty && !this.formDirty);
      }
    }
    return true;
  }

  setDefaultCountryCode(ctry: string = 'US'): void {
    if (ctry && this.phoneCodes) {
      const defaultCountry =
        this.phoneCodes.find((countryCode) => countryCode.code === ctry) ||
        this.phoneCodes.find((countryCode) => countryCode.code === 'US');
      this.profileForm.get('phoneCode').setValue(defaultCountry?.dial_code);
    }
  }

  onChange() {
    this.formDirty = true;
  }

  onCountryChange() {
    this.profileForm.get('postalCode').markAsTouched();
  }

 changePassword() {
  this.isChangePasswordDisabled = true;

  this.profSvc.getChangePassword()
    .pipe(takeUntil(this.onDestroy$))
    .subscribe({
      next: (ticket) => {
        window.location.assign(ticket);
      },
      error: (err) => {
        this.isChangePasswordDisabled = false;
      }
    });
  } 

  deleteUser() {
  this.isDeleteUserDisabled = true;
  const confDialog = this._dialog.open(DeleteConfimationDialogComponent,{
    autoFocus: false,
    disableClose:  true
  })

    confDialog.afterClosed().subscribe(result => {
      if (result) {
        this.profSvc.deleteUser()
          .pipe(takeUntil(this.onDestroy$))
          .subscribe({
            next: (success: boolean) => { 
              this._auth.logout();
            },
            error: (err) => { },
            complete: () => {
              this.isDeleteUserDisabled = false;
            }
          });
      }
      else{
          this.isDeleteUserDisabled = false;
      }
    });
  }

  private minLengthCheck(minLength: number) {
    return (control: any) => {
      if (!control.value) {
        return null;
      }
      const trimmedValue = control.value.trim();
      if (trimmedValue.length < minLength) {
        return { minLengthTrimmed: { requiredLength: minLength, actualLength: trimmedValue.length } };
      }
      return null;
    };
  }

  private maxLengthCheck(maxLength: number) {
    return (control: any) => {
      if (!control.value) {
        return null;
      }
      const trimmedValue = control.value.trim();
      if (trimmedValue.length > maxLength) {
        return { maxLengthTrimmed: { requiredLength: maxLength, actualLength: trimmedValue.length } };
      }
      return null;
    };
  }
}